import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute, RouterModule } from "@angular/router";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { CustomNotificationService } from "../../services/custom-notification.service";
import { AuthenticationService } from "../../services/authentication.service";
import { LucideAngularModule } from "lucide-angular";

@Component({
  selector: "app-password-reset",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    LucideAngularModule,
  ],
  templateUrl: "./password-reset.component.html",
  styleUrl: "./password-reset.component.css",
})
export class PasswordResetComponent implements OnInit {
  resetPasswordForm: FormGroup;
  loading = false;
  submitted = false;
  resetToken: string = "";
  email: string = "";
  resetSuccess = false;
  showPassword = false;
  showConfirmPassword = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: CustomNotificationService,
    private authService: AuthenticationService,
  ) {
    this.resetPasswordForm = this.formBuilder.group(
      {
        email: ["", [Validators.required, Validators.email]],
        newPassword: ["", [Validators.required, Validators.minLength(6)]],
        confirmPassword: ["", [Validators.required]],
      },
      {
        validators: this.passwordMatchValidator,
      },
    );
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.resetToken = params["token"] || params["verification"] || "";
      this.email = params["email"] || "";

      if (!this.resetToken) {
        const fullUrl = window.location.href;
        const questionMarkIndex = fullUrl.indexOf("?");

        if (questionMarkIndex !== -1) {
          const rawQueryString = fullUrl.substring(questionMarkIndex + 1);

          if (rawQueryString.startsWith("CfDJ8")) {
            this.resetToken = decodeURIComponent(rawQueryString);
          } else {
            const parts = rawQueryString.split("&");

            for (const part of parts) {
              const equalIndex = part.indexOf("=");

              if (equalIndex !== -1) {
                const key = part.substring(0, equalIndex);
                const value = part.substring(equalIndex + 1);

                if (
                  key.toLowerCase() === "token" ||
                  key.toLowerCase() === "verification"
                ) {
                  this.resetToken = decodeURIComponent(value);
                  break;
                }
              } else if (
                part.length > 20 &&
                !part.toLowerCase().startsWith("email")
              ) {
                this.resetToken = decodeURIComponent(part);
                break;
              }
            }
          }
        }
      }

      if (this.resetToken) {
        this.resetToken = this.resetToken.replace(/\s+/g, "+");
      }

      console.log("Reset token extracted:", this.resetToken);
      console.log("Token length:", this.resetToken?.length);
      console.log("Full URL:", window.location.href);

      if (this.email) {
        this.resetPasswordForm.patchValue({ email: this.email });
      }

      if (!this.resetToken || this.resetToken.trim() === "") {
        this.notificationService.error(
          "Invalid Link",
          "The password reset link is invalid or has expired.",
        );
        setTimeout(() => {
          this.router.navigate(["/forgot-password"]);
        }, 3000);
      }
    });
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get("newPassword");
    const confirmPassword = formGroup.get("confirmPassword");

    if (password && confirmPassword) {
      return password.value === confirmPassword.value
        ? null
        : { passwordMismatch: true };
    }
    return null;
  }

  get f() {
    return this.resetPasswordForm.controls;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.loading = true;
    const email = this.f["email"].value;
    const newPassword = this.f["newPassword"].value;

    console.log("Submitting password reset with:");
    console.log("Email:", email);
    console.log("New Password:", newPassword);
    console.log("Password length:", newPassword.length);
    console.log("Token:", this.resetToken);
    console.log("Token length:", this.resetToken.length);

    this.authService
      .resetPassword(email, newPassword, this.resetToken)
      .subscribe({
        next: (response) => {
          console.log("Password reset successful:", response);
          this.resetSuccess = true;
          this.loading = false;
          this.notificationService.success(
            "Password Reset Successfully",
            "Your password has been reset. You can now sign in with your new password.",
          );
          setTimeout(() => {
            this.router.navigate(["/login"]);
          }, 3000);
        },
        error: (error: any) => {
          console.error("Error resetting password:", error);
          console.error("Full error object:", JSON.stringify(error, null, 2));
          this.loading = false;
          const errorMessage =
            error?.error?.message ||
            error?.error ||
            error?.message ||
            "Failed to reset password. The link may have expired.";
          this.notificationService.error(
            "Reset Failed",
            typeof errorMessage === "string"
              ? errorMessage
              : "Failed to reset password",
          );
        },
      });
  }

  goBackToLogin(): void {
    this.router.navigate(["/login"]);
  }
}
